import { renderToStaticMarkup } from "react-dom/server";
import { AppType, DataPoints, getCitationFilePath } from "../../api";
import store from "../../Store";
import { normalizeAttachmentName, safeDecodeURIComponent } from "../../utils/AttachmentNameUtils";

export type HtmlParsedAnswer = {
    answerHtml: string;
    citations: string[];
    dataPoints: Array<DataPoints>;
};

export type AttachmentCitationInfo = {
    fileName: string;
    page?: number;
    displayText: string;
    token: string;
};

const ATTACHMENT_CITATION_PREFIX = "attachment:";
const ATTACHMENT_PAGE_CAPTURE_PATTERN = String.raw`(?:\s*(?:[#?][^\s<>()[\]]*?)?\b(?:page|pg|p)\s*=?\s*(\d+)|\s*\(?\s*(?:page|pg|p)\.?\s*[:=]?\s*(\d+)\s*\)?)?`;

type DataPoint = {
    sourcepage_field: string;
    uploaddir_field: string;
    doctype?: string;
    doc_url?: string;
};

type FileMappingObject = Record<string, unknown>;

export function isAttachmentCitationToken(citation: string): boolean {
    return citation.startsWith(ATTACHMENT_CITATION_PREFIX);
}

export function parseAttachmentCitationToken(citation: string): AttachmentCitationInfo | null {
    if (!isAttachmentCitationToken(citation)) return null;
    const raw = citation.slice(ATTACHMENT_CITATION_PREFIX.length);
    const parsed = extractAttachmentCitationFromText(raw);
    if (!parsed) return null;

    const token = buildAttachmentCitationToken(parsed.fileName, parsed.page);
    return {
        fileName: parsed.fileName,
        page: parsed.page,
        displayText: formatAttachmentCitationDisplay(parsed.fileName, parsed.page),
        token
    };
}

export function parseAnswerToHtml(
    answer: string,
    isStreaming: boolean,
    onCitationClicked: (citationFilePath: string, citationIndex: number) => void,
    dataPoints: DataPoint[],
    file_mapping: unknown,
    appType: AppType,
    sourcePage: "Chat" | "Feedback"
): HtmlParsedAnswer {
    // 1) Clean base text
    let parsedAnswer = repairCitationTags(answer.trim());
    const hasFileMapping = !!file_mapping && typeof file_mapping === "object" && Object.keys(file_mapping).length > 0;
    const knownCitationTokens = collectKnownCitationTokens(file_mapping, dataPoints);
    parsedAnswer = normalizeAlternateCitationBrackets(parsedAnswer, knownCitationTokens);

    // 1a) Keep attachment tags as citation-like bracket tokens so we can number them in answer body
    parsedAnswer = normalizeAttachmentTagsToBracketCitations(parsedAnswer);
    parsedAnswer = normalizeMalformedAttachmentMarkdownLinkSyntax(parsedAnswer);

    // 1b) file_mapping expansion
    if (file_mapping && typeof file_mapping === "object") {
        for (const key of Object.keys(file_mapping)) {
            const rawMappingValue = (file_mapping as FileMappingObject)[key];
            const resolvedMapping = resolveFileMappingText(rawMappingValue);
            if (!resolvedMapping) continue;

            const isAttachmentMapping = isAttachmentFileMappingEntry(key, resolvedMapping, rawMappingValue);
            if (isAttachmentMapping) {
                const mappedAttachment = extractAttachmentCitationFromText(resolvedMapping, getPageFromFileMappingValue(rawMappingValue));
                if (mappedAttachment) {
                    parsedAnswer = replaceAttachmentKeyWithCitationTokens(parsedAnswer, key, mappedAttachment);
                    continue;
                }
            }

            const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            // Case-insensitive like the backend, so "[Citation1.pdf]" still resolves.
            parsedAnswer = parsedAnswer.replace(new RegExp(String.raw`\[(?:<source>)?${escapedKey}(?:</source>)?\]`, "gi"), () => `[${resolvedMapping}]`);
            const bareKey = new RegExp(`\\b${escapedKey}\\b`, "gi");
            parsedAnswer = parsedAnswer.replace(bareKey, () => `[${resolvedMapping}]`);
        }
    }

    // 1c) Convert attachment links/path mentions into attachment citation tokens
    parsedAnswer = replaceInlineAttachmentMarkdownLinksWithCitationTokens(parsedAnswer);
    parsedAnswer = replaceInlineAttachmentPathReferencesWithCitationTokens(parsedAnswer);
    parsedAnswer = replaceInlineAttachmentHrefReferencesWithCitationTokens(parsedAnswer);
    parsedAnswer = replaceDoubleBracketAttachmentReferences(parsedAnswer);
    parsedAnswer = normalizeMalformedOpenAttachmentTokenSyntax(parsedAnswer);

    parsedAnswer = normalizeCitations(parsedAnswer);
    // Only once the mapping has arrived, so streaming output isn't stripped before keys can be resolved.
    if (hasFileMapping) {
        parsedAnswer = stripUnresolvedCitationKeys(parsedAnswer, knownCitationTokens);
        parsedAnswer = normalizeCitations(parsedAnswer);
    }

    // 3) During streaming, drop a dangling bracketed tail
    if (isStreaming) {
        let lastIndex = parsedAnswer.length;
        for (let i = parsedAnswer.length - 1; i >= 0; i--) {
            if (parsedAnswer[i] === "]") break;
            if (parsedAnswer[i] === "[") {
                lastIndex = i;
                break;
            }
        }
        parsedAnswer = parsedAnswer.substring(0, lastIndex);
    }

    // 4) Build valid citations from session datapoints (FAQ/Manuals/ALL)
    const overAllCurrentSessionDataPoints: {
        FAQ: Array<DataPoints>;
        Manuals: Array<DataPoints>;
        ALL: Array<DataPoints>;
    } = sourcePage === "Chat" ? store.getState().chatComponent.overAllCurrentSessionDataPoints : store.getState().feedBackPage.overAllCurrentSessionDataPoints;

    const appTypeDataPoints: Array<DataPoints> =
        appType === "ALL"
            ? overAllCurrentSessionDataPoints.ALL?.flat()
            : appType === "FAQ"
              ? overAllCurrentSessionDataPoints.FAQ?.flat()
              : overAllCurrentSessionDataPoints.Manuals?.flat();

    const validCitations = new Set(
        appTypeDataPoints.flatMap(item => [
            `${item.uploaddir_field}/${item.sourcepage_field}`,
            `${item.uploaddir_field}/`, // kept to match your original behavior; we’ll ignore trailing '/' later
            `${item.sourcepage_field}`
        ])
    );

    const knownSourceCitationFileNames = new Set(
        Array.from(validCitations)
            .map(citation => normalizeCitationFileNameForComparison(citation))
            .filter(Boolean)
    );
    const knownAttachmentFileNames = getKnownAttachmentFileNames(dataPoints);
    parsedAnswer = replaceBracketAttachmentReferencesWithCitationTokens(parsedAnswer, validCitations, knownSourceCitationFileNames, knownAttachmentFileNames);
    parsedAnswer = stripStrayCitationBrackets(parsedAnswer, token => (validCitations.has(token) && !token.endsWith("/")) || isAttachmentCitationToken(token));

    // 5) Tokenize text into [normalized citations] and plain text parts
    const parts = splitByValidCitationsSimple(parsedAnswer, validCitations, token => isAttachmentCitationToken(token));

    // 6) Collect citations by type first so source citations always come before attachment citations.
    const sourceCitations: string[] = [];
    const attachmentCitations: string[] = [];
    const sourceCitationSet = new Set<string>();
    const attachmentCitationSet = new Set<string>();
    const sourceCitationFileNameSet = new Set<string>();

    const registerCitationToken = (token: string) => {
        const isAttachmentCitation = isAttachmentCitationToken(token);
        if (token.endsWith("/") || (!validCitations.has(token) && !isAttachmentCitation)) return;

        if (isAttachmentCitation) {
            const attachmentInfo = parseAttachmentCitationToken(token);
            if (shouldSkipAttachmentCitationDueToSourceCollision(attachmentInfo, sourceCitationFileNameSet)) {
                return;
            }
            if (!attachmentCitationSet.has(token)) {
                attachmentCitationSet.add(token);
                attachmentCitations.push(token);
            }
            return;
        }

        if (!sourceCitationSet.has(token)) {
            sourceCitationSet.add(token);
            sourceCitations.push(token);
            const sourceFileName = normalizeCitationFileNameForComparison(token);
            if (sourceFileName) {
                sourceCitationFileNameSet.add(sourceFileName);
            }
        }
    };

    for (const part of parts) {
        const token = extractNormalizedCitation(part);
        if (!token) continue;
        registerCitationToken(token);
    }

    const fallbackAttachmentCitations = getAttachmentFallbackCitations(parsedAnswer, dataPoints);
    for (const fallbackToken of fallbackAttachmentCitations) {
        const fallbackInfo = parseAttachmentCitationToken(fallbackToken);
        if (shouldSkipAttachmentCitationDueToSourceCollision(fallbackInfo, sourceCitationFileNameSet)) {
            continue;
        }
        const hasSameAttachmentAlready = attachmentCitations.some(existingToken => {
            const existingInfo = parseAttachmentCitationToken(existingToken);
            return (
                !!fallbackInfo &&
                !!existingInfo &&
                existingInfo.fileName.toLowerCase() === fallbackInfo.fileName.toLowerCase() &&
                (existingInfo.page === fallbackInfo.page || Number.isFinite(existingInfo.page))
            );
        });
        if (!hasSameAttachmentAlready && !attachmentCitationSet.has(fallbackToken)) {
            attachmentCitationSet.add(fallbackToken);
            attachmentCitations.push(fallbackToken);
        }
    }

    const filteredAttachmentCitations = attachmentCitations.filter(token => {
        const attachmentInfo = parseAttachmentCitationToken(token);
        return !shouldSkipAttachmentCitationDueToSourceCollision(attachmentInfo, sourceCitationFileNameSet);
    });

    const citations: string[] = [...sourceCitations, ...filteredAttachmentCitations];
    const citationIndexMap = new Map<string, number>(citations.map((token, index) => [token, index]));
    const sourceCitationIndexMap = new Map<string, number>(sourceCitations.map((token, index) => [token, index + 1]));

    // 7) Render: valid citations -> numbered <sup>; everything else -> text
    const fragments: string[] = parts.map(part => {
        const token = extractNormalizedCitation(part);
        if (!token) {
            return part.endsWith("/") ? "" : part; // keep your trailing-slash guard
        }

        const isAttachmentCitation = isAttachmentCitationToken(token);
        if (token.endsWith("/") || (!validCitations.has(token) && !isAttachmentCitation)) {
            return `[${token}]`;
        }

        const idx = citationIndexMap.get(token);
        if (idx === undefined) {
            return `[${token}]`;
        }

        if (isAttachmentCitation) {
            const attachmentInfo = parseAttachmentCitationToken(token);
            return renderToStaticMarkup(
                <a className="supContainer" title={attachmentInfo?.displayText ?? token}>
                    <sup>{idx + 1}</sup>
                </a>
            );
        }

        const path = getCitationFilePath(token);
        const supportingCitationIndex = sourceCitationIndexMap.get(token) ?? idx + 1;
        return renderToStaticMarkup(
            <a className="supContainer" title={token} onClick={() => onCitationClicked(path, supportingCitationIndex)}>
                <sup>{idx + 1}</sup>
            </a>
        );
    });

    return {
        answerHtml: fragments.join(""),
        citations,
        dataPoints: appTypeDataPoints
    };
}

/**
 * Normalize/emit ONLY valid citations as separate parts.
 * Accepts:
 *   [<source>file</source>]
 *   [file]
 *   [<source>[file]</source>]
 *   or any wrapper like [foo <source>[file]</source> bar] -> extracts inner [file] if valid
 * Returns an array mixing:
 *   - plain text chunks
 *   - normalized citation chunks as "[token]" (no <source> wrappers)
 *
 * @internal Exported to support unit testing of citation parsing logic.
 * This is an implementation detail of the answer parsing pipeline and should not be
 * used directly by external consumers. Use parseAnswerToHtml() as the public API.
 */
export function splitByValidCitationsSimple(input: string, valid: Set<string>, isExtraValidToken?: (token: string) => boolean): string[] {
    const out: string[] = [];
    let i = 0,
        buf = "";

    const flush = () => {
        if (buf) {
            out.push(buf);
            buf = "";
        }
    };
    while (i < input.length) {
        if (input[i] === "[") {
            // try to find closing ]
            const j = input.indexOf("]", i + 1);
            if (j !== -1) {
                const inside = input.slice(i + 1, j).trim();
                if (valid.has(inside) || isExtraValidToken?.(inside)) {
                    flush();
                    out.push(`[${inside}]`);
                    i = j + 1;
                    continue;
                }

                // not valid -> keep as text
            }
        }
        buf += input[i++];
    }
    flush();
    return out;
}

const CITATION_SEPARATOR_ONLY = /^[ \t]*[,、，;；]?[ \t]*$/;
const TAGGED_CITATION_PATTERN = String.raw`<(?:source|attachment)>[^<>\[\]\r\n]*?</(?:source|attachment)>`;
const FILE_LIKE_LABEL = /\.[A-Za-z0-9]{1,10}(?:#\S*)?$/;
const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function collectKnownCitationTokens(fileMapping: unknown, dataPoints: DataPoint[] | undefined): Set<string> {
    const tokens = new Set<string>();
    if (fileMapping && typeof fileMapping === "object") {
        for (const [key, value] of Object.entries(fileMapping as FileMappingObject)) {
            tokens.add(key.trim());
            tokens.add(resolveFileMappingText(value));
        }
    }
    for (const point of dataPoints ?? []) {
        const sourcePage = (point?.sourcepage_field ?? "").trim();
        const uploadDir = (point?.uploaddir_field ?? "").trim();
        tokens.add(sourcePage);
        if (uploadDir && sourcePage) tokens.add(`${uploadDir}/${sourcePage}`);
    }
    tokens.delete("");
    return tokens;
}

/**
 * Convert citations wrapped in non-standard brackets — ［c］, 【c】, 〔c〕, (c), （c） — and markdown links pointing at a
 * citation ("[13_SDK.pdf](citation1.pdf)") to the standard "[c]" form. Only known citation keys/paths or
 * <source>/<attachment> tags are treated as citations, so text like "［プロパティ］" is left untouched.
 *
 * @internal Exported to support unit testing of citation parsing logic.
 */
export function normalizeAlternateCitationBrackets(input: string, knownTokens: Iterable<string>): string {
    const tokens = Array.from(new Set(Array.from(knownTokens).map(t => t.trim()))).filter(Boolean);
    tokens.sort((a, b) => b.length - a.length);
    const core = `(?:${[TAGGED_CITATION_PATTERN, ...tokens.map(escapeRegExp)].join("|")})`;
    const coreOnly = new RegExp(`^${core}$`, "i");

    const withLinks = input.replace(new RegExp(String.raw`\[([^[\]\r\n]*)\]\(\s*(${core})\s*\)`, "gi"), (_m, rawLabel: string, target: string) => {
        const label = rawLabel.trim();
        return !label || FILE_LIKE_LABEL.test(label) || coreOnly.test(label) ? `[${target}]` : `${label}[${target}]`;
    });
    return withLinks.replace(
        new RegExp(String.raw`[［【〔（(][ \t\u3000]*\[?[ \t]*(${core})[ \t]*\]?[ \t\u3000]*[］】〕）)]`, "gi"),
        (_m, citation: string) => `[${citation}]`
    );
}

/**
 * Drop "[citationN.ext]" / "[attachmentN]" prompt keys that don't map to any retrieved document.
 *
 * @internal Exported to support unit testing of citation parsing logic.
 */
export function stripUnresolvedCitationKeys(input: string, knownTokens: Set<string>): string {
    const knownLower = new Set(Array.from(knownTokens).map(t => t.toLowerCase()));
    const stripped = input.replace(/[ \t]?\[[ \t]*((?:citation|attachment)\d+(?:\.[A-Za-z0-9]{1,10})?)[ \t]*\]/gi, (match: string, key: string) =>
        knownLower.has(key.toLowerCase()) ? match : ""
    );
    return stripped.replace(/\[[ \t]*(?:[,、，;；][ \t]*)+(\[[^\]\r\n]+\])[ \t]*\]/g, "$1").replace(/\[[ \t]*(?:[,、，;；][ \t]*)+\]/g, "");
}

/**
 * Canonicalize <source>/<attachment> tags and add a missing ">" (e.g. "[<source>a</source]" -> "[<source>a</source>]").
 *
 * @internal Exported to support unit testing of citation parsing logic.
 */
export function repairCitationTags(input: string): string {
    return input
        .replace(/(\[[ \t]*)(attachment|source)[ \t]*>/gi, (_m, prefix: string, tag: string) => `${prefix}<${tag}>`)
        .replace(
            /(?<!<[ \t]*)(^|[\s[\]]|\.[A-Za-z0-9]{1,10}(?:#[^\s<>[\]]*)?)\/[ \t]*(attachment|source)[ \t]*>/gi,
            (_m, before: string, tag: string) => `${before}</${tag}>`
        )
        .replace(/<[ \t]*(\/?)[ \t]*(attachment|source)\b[ \t]*>?/gi, (_m, slash: string, tag: string) => `<${slash}${tag.toLowerCase()}>`);
}

/**
 * Remove wrapper/stray square brackets around groups of valid citations so they don't render as
 * literal "[" / "]" next to the superscripts: "[[a][b]]", "[[a], [b]]", "[ [a]", "[a]]" -> "[a][b]" / "[a]".
 *
 * @internal Exported to support unit testing of citation parsing logic.
 */
export function stripStrayCitationBrackets(input: string, isValidToken: (token: string) => boolean): string {
    const spans: Array<{ start: number; end: number; token: string }> = [];
    for (const match of input.matchAll(/\[([^[\]\r\n]+)\]/g)) {
        const token = match[1].trim();
        if (match.index !== undefined && isValidToken(token)) {
            spans.push({ start: match.index, end: match.index + match[0].length, token });
        }
    }
    if (!spans.length) return input;

    let out = "";
    let cursor = 0;
    let i = 0;
    while (i < spans.length) {
        let j = i;
        while (j + 1 < spans.length && CITATION_SEPARATOR_ONLY.test(input.slice(spans[j].end, spans[j + 1].start))) j++;

        let before = input.slice(cursor, spans[i].start);
        const strayOpen = before.match(/\[[ \t]*$/);
        if (strayOpen) before = before.slice(0, before.length - strayOpen[0].length);
        out += before;
        for (let k = i; k <= j; k++) out += `[${spans[k].token}]`;

        cursor = spans[j].end;
        const strayClose = input.slice(cursor).match(/^[ \t]*[,、，;；]?[ \t]*\](?!\()/);
        if (strayClose) cursor += strayClose[0].length;
        i = j + 1;
    }
    return out + input.slice(cursor);
}

/** If part is a normalized citation like "[token]", return "token"; otherwise null */
export function extractNormalizedCitation(part: string): string | null {
    const matched = part.match(/^\[\s*([^\]]+?)\s*\]$/);
    return matched ? matched[1].trim() : null;
}

export function normalizeCitations(text: string): string {
    let prev: string;
    do {
        prev = text;
        // Case A: [ <source> filename </source> ]  -> [filename] (an empty tag pair is dropped)
        text = text.replace(/\[\s*<\s*source\s*>\s*([^<>\r\n]*?)\s*<\s*\/\s*source\s*>\s*\]/gi, (_, p1) => bracketCitation(p1));
        // Case A2: bare/grouped <source> filename </source> (e.g. "[<source>a</source>, <source>b</source>]") -> [filename]
        text = text.replace(/<\s*source\s*>\s*([^<>\r\n]*?)\s*<\s*\/\s*source\s*>/gi, (_, p1) => bracketCitation(p1));
        // Case A3: orphan/unpaired source tags left by malformed output (e.g. "[[file]</source>]");
        // a one-sided tag next to a file name still becomes a citation ("<source>a.pdf" -> "[a.pdf]").
        text = text.replace(/\[[ \t]*<\s*\/?\s*source\s*>[ \t]*\]/gi, "");
        text = wrapOneSidedFileCitationTags(text, "source");
        text = text.replace(/<\s*\/?\s*source\s*>/gi, "");
        // Case B: double brackets [[ filename ]] -> [filename]
        text = text.replace(/\[\s*\[\s*([^\]]+?)\s*\]\s*\]/g, (_, p1) => `[${p1.trim()}]`);
        // Case C: trim spaces inside single brackets [  token  ] -> [token]
        text = text.replace(/\[\s*([^\]]+?)\s*\]/g, (_, p1) => `[${p1.trim()}]`);
    } while (text !== prev); // iterate until stable
    return text;
}

function cleanAttachmentDisplayName(value?: string): string {
    return normalizeAttachmentName(value, {
        lowerCase: false,
        trimTrailingPunctuation: true,
        trailingPunctuationPattern: /[.,;:]+$/
    });
}

function hasFileExtension(value: string): boolean {
    return /\.[a-z0-9]{1,10}$/i.test(value);
}

function normalizeCitationFileNameForComparison(value?: string): string {
    if (!value) return "";
    const normalizedFileName = cleanAttachmentDisplayName(value).toLowerCase();
    return hasFileExtension(normalizedFileName) ? normalizedFileName : "";
}

function shouldSkipAttachmentCitationDueToSourceCollision(attachmentInfo: AttachmentCitationInfo | null, sourceCitationFileNameSet: Set<string>): boolean {
    if (!attachmentInfo || Number.isFinite(attachmentInfo.page)) return false;
    const normalizedAttachmentName = normalizeCitationFileNameForComparison(attachmentInfo.fileName);
    if (!normalizedAttachmentName) return false;
    return sourceCitationFileNameSet.has(normalizedAttachmentName);
}

function normalizeAttachmentTagsToBracketCitations(text: string): string {
    let next = text;
    // Tag content stays on one line so an unclosed tag can't swallow text up to a later closing tag.
    // Empty tags ("[<attachment>]", "[<attachment></attachment>]") carry no citation and are dropped.
    next = next.replace(/\[\s*<\s*attachment\s*>\s*([^<>\r\n]*?)\s*<\s*\/\s*attachment\s*>\s*\]/gi, (_m, p1) => bracketCitation(p1));
    next = next.replace(/<\s*attachment\s*>\s*([^<>\r\n]*?)\s*<\s*\/\s*attachment\s*>/gi, (_m, p1) => bracketCitation(p1));
    next = next.replace(/\[[ \t]*<\s*\/?\s*attachment\s*>[ \t]*\]/gi, "");
    // Open-only bracketed wrapper: "[<attachment>file.pdf]" -> "[file.pdf]"
    next = next.replace(/\[[ \t]*<\s*attachment\s*>\s*([^<>[\]\r\n]+?)[ \t]*\]/gi, (_m, p1) => `[${String(p1).trim()}]`);
    next = wrapOneSidedFileCitationTags(next, "attachment");
    // Recover malformed angle wrappers like: [<[[file.pdf#page=4]>]
    next = next.replace(/\[\s*<\s*\[\[([\s\S]*?)\]\s*>\s*\]/gi, (_m, p1) => {
        const normalizedInner = recoverLikelyMissingLeadingBracket(String(p1).trim());
        return `[[${normalizedInner}]]`;
    });
    next = next.replace(/<\s*\[\[([\s\S]*?)\]\s*>/gi, (_m, p1) => {
        const normalizedInner = recoverLikelyMissingLeadingBracket(String(p1).trim());
        return `[[${normalizedInner}]]`;
    });
    // Recover malformed outputs like: [<[[file.pdf#page=6]</attachment>]
    next = next.replace(/\[\s*<\s*\[\[([\s\S]*?)\]\s*<\s*\/\s*attachment\s*>\s*\]/gi, (_m, p1) => {
        const normalizedInner = recoverLikelyMissingLeadingBracket(String(p1).trim());
        return `[[${normalizedInner}]]`;
    });
    // Recover malformed outputs where the opening <attachment> tag is missing.
    next = next.replace(/\]\s*<\s*\/\s*attachment\s*>\s*\]/gi, "]]");
    // Drop any remaining stray opening/closing attachment tags.
    next = next.replace(/<\s*\/?\s*attachment\s*>/gi, "");
    return next;
}

const ONE_SIDED_FILE_TOKEN = String.raw`[A-Za-z0-9][A-Za-z0-9._%/\\+-]*\.[A-Za-z0-9]{1,10}(?:#[A-Za-z0-9=&._-]*)?`;

const bracketCitation = (inner: unknown): string => {
    const token = String(inner ?? "").trim();
    return token ? `[${token}]` : "";
};

/**
 * Wrap a file-like token that has only an opening or only a closing tag as a citation:
 * "<source>a.pdf text" -> "[a.pdf] text", "text a.pdf</source>" -> "text [a.pdf]". Ordinary words are not wrapped.
 */
function wrapOneSidedFileCitationTags(text: string, tag: "source" | "attachment"): string {
    return text
        .replace(new RegExp(String.raw`<\s*${tag}\s*>\s*(${ONE_SIDED_FILE_TOKEN})`, "gi"), (_m, token: string) => `[${token}]`)
        .replace(new RegExp(String.raw`(?<!\S)(${ONE_SIDED_FILE_TOKEN})\s*<\s*\/\s*${tag}\s*>`, "gi"), (_m, token: string) => `[${token}]`);
}

function normalizeMalformedAttachmentMarkdownLinkSyntax(text: string): string {
    let next = text;
    // Recover malformed markdown links like: [[file]]([[file]) to avoid duplicate/garbled captures.
    next = next.replace(/\]\]\s*\(\s*\[\[([\s\S]*?)\]\)?\s*\)/gi, "]]");
    return next;
}

function recoverLikelyMissingLeadingBracket(value: string): string {
    const normalized = value.trim();
    if (!normalized) return normalized;
    const firstClosingBracket = normalized.indexOf("]");
    const firstOpeningBracket = normalized.indexOf("[");
    if (firstClosingBracket > 0 && (firstOpeningBracket === -1 || firstOpeningBracket > firstClosingBracket)) {
        return `[${normalized}`;
    }
    return normalized;
}

function resolveFileMappingText(value: unknown): string {
    if (typeof value === "string" || typeof value === "number") {
        return String(value).trim();
    }
    if (!value || typeof value !== "object") return "";

    const objectValue = value as Record<string, unknown>;
    const uploadDir = String(objectValue.uploaddir_field ?? objectValue.uploadDir ?? "").trim();
    const sourcePage = String(objectValue.sourcepage_field ?? objectValue.sourcePage ?? objectValue.sourcepage ?? "").trim();
    if (uploadDir && sourcePage) {
        return `${uploadDir}/${sourcePage}`;
    }

    const candidateFields = [
        "path",
        "blob_path",
        "blobPath",
        "source",
        "sourcepage",
        "sourcePage",
        "citation",
        "value",
        "url",
        "href",
        "doc_url",
        "docUrl",
        "file_name",
        "fileName",
        "name",
        "filename"
    ];
    for (const field of candidateFields) {
        const candidate = objectValue[field];
        if (typeof candidate === "string" && candidate.trim()) {
            return candidate.trim();
        }
    }
    return "";
}

function getPageFromFileMappingValue(value: unknown): number | undefined {
    if (!value || typeof value !== "object") return undefined;
    const objectValue = value as Record<string, unknown>;
    const pageCandidates = [objectValue.page, objectValue.page_no, objectValue.pageNo, objectValue.page_number, objectValue.pageNumber];
    for (const pageCandidate of pageCandidates) {
        const pageNumber = Number(pageCandidate);
        if (Number.isFinite(pageNumber)) {
            return pageNumber;
        }
    }
    return undefined;
}

function isAttachmentFileMappingEntry(key: string, resolvedValue: string, rawValue: unknown): boolean {
    const keyLower = key.toLowerCase();
    const valueLower = resolvedValue.toLowerCase();
    if (keyLower.startsWith("attachment")) return true;
    if (valueLower.includes("attachments/") || valueLower.startsWith("attachment://") || valueLower.includes("/attachments/")) return true;

    if (rawValue && typeof rawValue === "object") {
        const objectValue = rawValue as Record<string, unknown>;
        const typeHints = [objectValue.type, objectValue.doctype, objectValue.category, objectValue.source_type, objectValue.sourceType]
            .map(item => String(item ?? "").toLowerCase())
            .filter(Boolean);
        if (typeHints.some(item => item.includes("attachment") || item.includes("user file"))) {
            return true;
        }
    }
    return false;
}

function parseOptionalPage(pageA?: string, pageB?: string): number | undefined {
    const pageValue = pageA || pageB;
    if (!pageValue) return undefined;
    const parsed = Number(pageValue);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function buildAttachmentCitationToken(fileName: string, page?: number): string {
    const cleanedFileName = cleanAttachmentDisplayName(fileName);
    if (!cleanedFileName) return "";
    const encodedFileName = encodeURIComponent(cleanedFileName);
    if (Number.isFinite(page)) {
        return `${ATTACHMENT_CITATION_PREFIX}${encodedFileName}#page=${page}`;
    }
    return `${ATTACHMENT_CITATION_PREFIX}${encodedFileName}`;
}

function formatAttachmentCitationDisplay(fileName: string, page?: number): string {
    const normalizedName = cleanAttachmentDisplayName(fileName);
    if (Number.isFinite(page)) {
        return `${normalizedName} (page=${page})`;
    }
    return normalizedName;
}

function extractAttachmentCitationFromText(rawValue?: string, forcedPage?: number): { fileName: string; page?: number } | null {
    if (!rawValue) return null;
    let candidate = safeDecodeURIComponent(String(rawValue)).trim();
    if (!candidate) return null;
    candidate = candidate.replace(/^<+/, "").replace(/>+$/, "").trim();
    candidate = normalizeMalformedAttachmentCandidate(candidate);

    if (candidate.toLowerCase().includes("attachment://")) {
        candidate = candidate.replace(/^.*?attachment:\/\//i, "");
    } else {
        const attachmentsStart = candidate.toLowerCase().indexOf("attachments/");
        if (attachmentsStart >= 0) {
            candidate = candidate.slice(attachmentsStart);
        }
    }

    candidate = candidate.replace(/[.,;:'"]+\s*$/, "").trim();

    let page: number | undefined = Number.isFinite(forcedPage) ? forcedPage : undefined;
    const pageMatch =
        candidate.match(/[?#][^#?]*\b(?:page|pg|p)\s*=?\s*(\d+)\s*[\])>]*$/i) ||
        candidate.match(/\(\s*(?:page|pg|p)\s*[:=]?\s*(\d+)\s*\)\s*[\])>]*$/i) ||
        candidate.match(/\b(?:page|pg|p)\s*[:=]?\s*(\d+)\s*[\])>]*$/i);

    if (!page && pageMatch?.[1]) {
        const parsed = Number(pageMatch[1]);
        if (Number.isFinite(parsed)) {
            page = parsed;
        }
    }

    if (pageMatch?.[0]) {
        candidate = candidate.replace(pageMatch[0], "").trim();
    }

    candidate = candidate.replace(/[)\],.;:'"]+$/, "").trim();
    candidate = candidate.replace(/^attachments\//i, "").trim();
    const candidateSegments = candidate.split("/").filter(Boolean);
    const fileNameCandidate = candidateSegments.length > 0 ? candidateSegments[candidateSegments.length - 1] : candidate;
    const fileName = normalizeAttachmentFileName(fileNameCandidate);
    if (!fileName || !hasFileExtension(fileName)) return null;

    return {
        fileName,
        page: Number.isFinite(page) ? page : undefined
    };
}

function normalizeMalformedAttachmentCandidate(value: string): string {
    let normalized = value.trim();
    // Merge concatenated malformed fragments from markdown links.
    normalized = normalized.replace(/\]\]\s*\(\s*\[\[/g, "[[");
    normalized = normalized.replace(/\]\)\s*\[\[/g, "[[");
    normalized = normalized.replace(/\]\s*\[\[/g, "[[");

    const lastDoubleBracketIndex = normalized.lastIndexOf("[[");
    if (lastDoubleBracketIndex > 0) {
        normalized = normalized.slice(lastDoubleBracketIndex + 1);
    }
    return normalized.trim();
}

function normalizeAttachmentFileName(value: string): string {
    let normalized = cleanAttachmentDisplayName(value);
    normalized = normalized.replace(/^\[\[/, "[");
    normalized = recoverLikelyMissingLeadingBracket(normalized);
    return normalized;
}

function replaceAttachmentKeyWithCitationTokens(answer: string, key: string, mappedAttachment: { fileName: string; page?: number }): string {
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const replacement = (pageA?: string, pageB?: string): string => {
        const overridePage = parseOptionalPage(pageA, pageB);
        const token = buildAttachmentCitationToken(mappedAttachment.fileName, Number.isFinite(overridePage) ? overridePage : mappedAttachment.page);
        return token ? `[${token}]` : `[${mappedAttachment.fileName}]`;
    };

    let parsedAnswer = answer;
    parsedAnswer = parsedAnswer.replace(
        new RegExp(`\\[\\s*<\\s*attachment\\s*>\\s*(${escapedKey})${ATTACHMENT_PAGE_CAPTURE_PATTERN}\\s*<\\s*\\/\\s*attachment\\s*>\\s*\\]`, "gi"),
        (_m, _k, pageA, pageB) => replacement(pageA, pageB)
    );
    parsedAnswer = parsedAnswer.replace(
        new RegExp(`<\\s*attachment\\s*>\\s*(${escapedKey})${ATTACHMENT_PAGE_CAPTURE_PATTERN}\\s*<\\s*\\/\\s*attachment\\s*>`, "gi"),
        (_m, _k, pageA, pageB) => replacement(pageA, pageB)
    );
    parsedAnswer = parsedAnswer.replace(
        new RegExp(`\\[\\s*<\\s*source\\s*>\\s*(${escapedKey})${ATTACHMENT_PAGE_CAPTURE_PATTERN}\\s*<\\s*\\/\\s*source\\s*>\\s*\\]`, "gi"),
        (_m, _k, pageA, pageB) => replacement(pageA, pageB)
    );
    parsedAnswer = parsedAnswer.replace(new RegExp(`\\[\\s*(${escapedKey})${ATTACHMENT_PAGE_CAPTURE_PATTERN}\\s*\\]`, "gi"), (_m, _k, pageA, pageB) =>
        replacement(pageA, pageB)
    );
    parsedAnswer = parsedAnswer.replace(
        new RegExp(`(^|[^a-zA-Z0-9_])(${escapedKey})${ATTACHMENT_PAGE_CAPTURE_PATTERN}`, "gi"),
        (_m, prefix, _k, pageA, pageB) => {
            return `${prefix}${replacement(pageA, pageB)}`;
        }
    );
    return parsedAnswer;
}

function replaceInlineAttachmentMarkdownLinksWithCitationTokens(answer: string): string {
    return answer.replace(/\[(?:\\.|[^\\\]])*?\]\((attachment:\/\/[^\s)]+)\)/gi, (match: string, href: string) => {
        const attachment = extractAttachmentCitationFromText(href);
        if (!attachment) return match;
        const token = buildAttachmentCitationToken(attachment.fileName, attachment.page);
        return token ? `[${token}]` : match;
    });
}

function replaceInlineAttachmentPathReferencesWithCitationTokens(answer: string): string {
    const attachmentPathRegex =
        /attachments\/[^<>\r\n\t]+(?:\.[a-z0-9]{1,10})+(?:\s*\(?\s*(?:page|pg|p)\.?\s*[:=]?\s*\d+\s*\)?|[?#][^\s<>()[\]]*\b(?:page|pg|p)\s*=?\s*\d+)?/gi;
    return answer.replace(attachmentPathRegex, (match: string) => {
        const attachment = extractAttachmentCitationFromText(match);
        if (!attachment) return match;
        const token = buildAttachmentCitationToken(attachment.fileName, attachment.page);
        return token ? `[${token}]` : match;
    });
}

function replaceInlineAttachmentHrefReferencesWithCitationTokens(answer: string): string {
    return answer.replace(/attachment:\/\/[^\s)\]]+/gi, (match: string) => {
        const attachment = extractAttachmentCitationFromText(match);
        if (!attachment) return match;
        const token = buildAttachmentCitationToken(attachment.fileName, attachment.page);
        return token ? `[${token}]` : match;
    });
}

function replaceDoubleBracketAttachmentReferences(answer: string): string {
    return answer.replace(/\[\[([\s\S]*?)\]\]/g, (match: string, inner: string) => {
        const attachment = extractAttachmentCitationFromText(inner);
        if (!attachment) return match;
        const token = buildAttachmentCitationToken(attachment.fileName, attachment.page);
        return token ? `[${token}]` : match;
    });
}

function normalizeMalformedOpenAttachmentTokenSyntax(answer: string): string {
    let next = answer;
    // Recover malformed link label remnants like: "[Open ... [attachment:...]" -> "Open ... [attachment:...]"
    next = next.replace(/\[\s*(Open[^[]*?)\s*(?=\[\s*attachment:[^\]]+\])/gi, (_m, label) => `${String(label).trim()} `);
    // Remove stray "(" before attachment token: "Open the paper ([attachment:...]" -> "Open the paper [attachment:...]"
    next = next.replace(/(Open[^[]*?)\(\s*(?=\[\s*attachment:[^\]]+\])/gi, (_m, label) => `${String(label).trim()} `);
    // Collapse consecutive duplicate attachment tokens produced by malformed markdown link tails.
    next = next.replace(/\[\s*(attachment:[^\]]+)\s*\]\s+\[\s*\1\s*\]/gi, "[$1]");
    return next;
}

function replaceBracketAttachmentReferencesWithCitationTokens(
    answer: string,
    validCitations: Set<string>,
    knownSourceCitationFileNames: Set<string>,
    knownAttachmentFileNames: Set<string>
): string {
    return answer.replace(/\[([^\]\r\n]+)\]/g, (match: string, inner: string) => {
        const token = String(inner ?? "").trim();
        if (!token || isAttachmentCitationToken(token)) return match;
        if (/<\s*\/?\s*(source|attachment)\s*>/i.test(token)) return match;

        // Check if this token resolves to a known attachment file name.
        // Known attachment files must always become attachment citation tokens,
        // even when their bare filename also appears in validCitations (which
        // happens because user-file data points contribute to validCitations
        // via sourcepage_field). Without this priority check, bare filenames
        // like [report.docx] would be treated as source citations, colliding
        // with the attachment:report.docx tokens and causing both rendering
        // failures and a spurious Sources-tab button.
        const attachment = extractAttachmentCitationFromText(token);
        if (attachment) {
            const normalizedAttachmentName = normalizeCitationFileNameForComparison(attachment.fileName);
            if (normalizedAttachmentName && knownAttachmentFileNames.has(normalizedAttachmentName)) {
                const attachmentToken = buildAttachmentCitationToken(attachment.fileName, attachment.page);
                return attachmentToken ? `[${attachmentToken}]` : match;
            }
        }

        // Not a known attachment file – skip valid source citations
        if (validCitations.has(token)) return match;

        const tokenLower = token.toLowerCase();
        const hasSlash = token.includes("/") || token.includes("\\");
        const hasAttachmentPathHint = tokenLower.includes("attachments/") || tokenLower.startsWith("attachment://") || tokenLower.includes("/attachments/");
        if (hasSlash && !hasAttachmentPathHint) return match;

        if (!attachment) return match;

        const normalizedAttachmentName = normalizeCitationFileNameForComparison(attachment.fileName);
        if (!normalizedAttachmentName) return match;
        if (!hasAttachmentPathHint) return match;
        if (!Number.isFinite(attachment.page) && knownSourceCitationFileNames.has(normalizedAttachmentName)) return match;

        const attachmentToken = buildAttachmentCitationToken(attachment.fileName, attachment.page);
        return attachmentToken ? `[${attachmentToken}]` : match;
    });
}

function isAttachmentDataPoint(point: DataPoint): boolean {
    const uploadDir = (point?.uploaddir_field ?? "").toString().toLowerCase();
    const sourcePage = (point?.sourcepage_field ?? "").toString().toLowerCase();
    const docType = (point?.doctype ?? "").toString().toLowerCase();
    const docUrl = (point?.doc_url ?? "").toString().toLowerCase();
    return (
        uploadDir.startsWith("attachments/") ||
        sourcePage.includes("attachments/") ||
        uploadDir === "user_files" ||
        docType === "user file" ||
        docUrl.includes("/attachments/")
    );
}

function getKnownAttachmentFileNames(dataPoints: DataPoint[]): Set<string> {
    const fileNames = new Set<string>();
    if (!Array.isArray(dataPoints)) return fileNames;

    for (const point of dataPoints) {
        if (!isAttachmentDataPoint(point)) continue;
        const uploadDir = (point?.uploaddir_field ?? "").toString();
        const sourcePage = (point?.sourcepage_field ?? "").toString();
        const fileName = normalizeCitationFileNameForComparison(sourcePage || uploadDir);
        if (fileName) {
            fileNames.add(fileName);
        }
    }
    return fileNames;
}

function getAttachmentFallbackCitations(answer: string, dataPoints: DataPoint[]): string[] {
    if (!answer || !Array.isArray(dataPoints) || dataPoints.length === 0) return [];
    const answerLower = answer.toLowerCase();
    const tokens: string[] = [];
    const seen = new Set<string>();

    for (const point of dataPoints) {
        const uploadDir = (point?.uploaddir_field ?? "").toString();
        const sourcePage = (point?.sourcepage_field ?? "").toString();
        if (!isAttachmentDataPoint(point)) continue;

        const fileName = cleanAttachmentDisplayName(sourcePage || uploadDir);
        if (!fileName || !hasFileExtension(fileName)) continue;
        const encodedFileName = encodeURIComponent(fileName).toLowerCase();
        const normalizedFileName = fileName.toLowerCase();
        if (!answerLower.includes(normalizedFileName) && !answerLower.includes(encodedFileName)) continue;

        const token = buildAttachmentCitationToken(fileName);
        if (!token || seen.has(token)) continue;
        seen.add(token);
        tokens.push(token);
    }

    return tokens;
}
